import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "@/api";
import { ArrowLeft, Lock, Building2, User, ShieldCheck, Flag, HeartHandshake, Handshake } from "lucide-react";
import { LanguageCurrencySwitcher } from "@/components/LanguageCurrencySwitcher";

const roleConfig = {
  chef: { title: "Chef d'entreprise", sub: "Connexion avec votre code entreprise", placeholder: "PAG-ENT-8492", icon: Building2 },
  salarie: { title: "Salarié", sub: "Connexion avec votre code personnel", placeholder: "PAG-SAL-2391", icon: User },
  admin: { title: "Admin PAG", sub: "Accès administrateur & équipe PAG", placeholder: "PAG-ADMIN-001 ou PAG-TEAM-1234", icon: ShieldCheck },
  syndicat: { title: "Syndicat", sub: "Espace organisation syndicale", placeholder: "PAG-SYNDIC-0142", icon: Flag },
  association: { title: "Association / ONG", sub: "Espace organisation associative", placeholder: "PAG-ASSO-ONG-0142", icon: HeartHandshake },
  bp: { title: "Business Partner", sub: "Espace apporteur d'affaires indépendant", placeholder: "PAG-BP-0142", icon: Handshake },
};

export default function Login() {
  const { roleHint } = useParams();
  const cfg = roleConfig[roleHint] || roleConfig.chef;
  const Icon = cfg.icon;
  const { login } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(code, password, needsTotp ? totpCode : undefined);
      toast.success(`Bienvenue ${user.prenom} ${user.nom}`);
      if (user.role === "admin" || user.role === "staff") navigate("/admin");
      else if (user.role === "chef") navigate("/chef");
      else if (user.role === "syndicat") navigate("/syndicat");
      else if (user.role === "association") navigate("/association");
      else if (user.role === "bp") navigate("/bp");
      else navigate("/salarie");
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      // Backend renvoie "Code 2FA requis" si user.totp_enabled mais aucun code fourni
      if (typeof detail === "string" && /2FA/i.test(detail) && !needsTotp) {
        setNeedsTotp(true);
        toast.info("Code 2FA requis pour ce compte");
      } else {
        toast.error(formatApiError(detail));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white text-[#0A0A0A]">
      {/* LEFT visual */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#0A0A0A] text-white p-12 flex-col justify-between overflow-hidden">
        <Link to="/" data-testid="login-back-home" className="flex items-center gap-2 text-sm uppercase tracking-[0.25em] font-semibold hover:text-[#FFD700] transition-colors w-fit">
          <ArrowLeft className="h-4 w-4" /> Accueil
        </Link>
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#FFD700] font-semibold mb-6">// Authentification</div>
          <h2 className="font-display text-5xl xl:text-6xl font-black tracking-tighter leading-[0.95]">
            Plateforme<br />
            RH sécurisée.
          </h2>
          <p className="mt-6 text-base text-white/60 max-w-md leading-relaxed">
            Votre accès est protégé par un code unique et un mot de passe. Les données sont cloisonnées par entreprise.
          </p>
        </div>
        <div className="border-t border-white/20 pt-6 flex items-center justify-between text-xs uppercase tracking-[0.25em] font-semibold">
          <span>PAG · 2026</span>
          <span className="flex items-center gap-2">
            <Lock className="h-3 w-3" /> Connexion chiffrée
          </span>
        </div>
      </div>

      {/* RIGHT form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-16 relative">
        {/* v2.0.11 — Language/Currency switcher */}
        <div className="absolute top-4 right-4 lg:top-6 lg:right-6"><LanguageCurrencySwitcher /></div>
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 text-sm font-semibold mb-8" data-testid="login-back-home-mobile">
            <ArrowLeft className="h-4 w-4" /> Retour
          </Link>

          <div className="flex items-center gap-3 mb-8">
            <div className="h-12 w-12 border border-[#0A0A0A] flex items-center justify-center">
              <Icon className="h-6 w-6 text-[#002FA7]" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#868E96] font-semibold">Connexion</div>
              <h1 className="font-display text-2xl font-black tracking-tight" data-testid="login-role-title">{cfg.title}</h1>
            </div>
          </div>

          <p className="text-sm text-[#495057] mb-8">{cfg.sub}</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="code" className="text-xs uppercase tracking-[0.2em] font-semibold">Code d'accès</Label>
              <Input
                id="code"
                data-testid="login-code-input"
                placeholder={cfg.placeholder}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="h-12 font-mono text-sm border-[#DEE2E6] focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 rounded-md"
                required
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] font-semibold">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 border-[#DEE2E6] focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 rounded-md"
                required
              />
            </div>
            {needsTotp && (
              <div className="space-y-2 p-3 bg-[#FFFBEB] border border-[#FFD400]/40" data-testid="login-totp-block">
                <Label htmlFor="totp" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D97706]">Code 2FA (6 chiffres)</Label>
                <Input
                  id="totp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  data-testid="login-totp-input"
                  placeholder="000000"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                  className="h-12 text-center font-mono text-2xl tracking-widest"
                  required
                  autoFocus
                />
                <div className="text-[10px] text-[#868E96]">Saisissez le code généré par votre application d'authentification</div>
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-12 bg-[#002FA7] hover:bg-[#002175] text-white rounded-md font-semibold"
            >
              {loading ? "Connexion…" : "Se connecter"}
            </Button>
          </form>

          {/* Switch role */}
          <div className="mt-10 pt-6 border-t border-[#DEE2E6]">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#868E96] mb-3">Autre espace</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(roleConfig).filter(([k]) => k !== roleHint).map(([k, c]) => (
                <Link
                  key={k}
                  to={`/login/${k}`}
                  data-testid={`login-switch-${k}`}
                  className="text-xs uppercase tracking-[0.2em] font-semibold px-3 py-2 border border-[#DEE2E6] hover:border-[#0A0A0A] transition-colors"
                >
                  {c.title}
                </Link>
              ))}
            </div>
          </div>

          {roleHint === "admin" && (
            <div className="mt-8 p-4 border border-[#DEE2E6] bg-[#F8F9FA] text-xs text-[#495057]">
              Accès réservé aux administrateurs PAG.
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-[#DEE2E6]" data-testid="login-support">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#868E96] mb-2">Besoin d'aide ?</div>
            <div className="space-y-1 text-sm">
              <a href="mailto:contact@parcavenue.eu" className="block hover:text-[#002FA7] transition-colors">
                contact@parcavenue.eu
              </a>
              <a href="tel:+33970701792" className="block hover:text-[#002FA7] transition-colors font-mono">
                09 70 70 17 92
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
